import React, { useEffect, useRef, useState } from 'react';
import { Checkbox, Popper } from '@mui/material';
import { normalizarSelecaoProjetos, rotuloProjetos, ProjetoOpcao } from '../../utils/filtroProjetos';

interface FiltroProjetosMultiProps {
  projetos: ProjetoOpcao[];
  /** Projetos aplicados. Lista vazia = todos (sem filtro). */
  selecionados: string[];
  onChange: (uids: string[]) => void;
}

// Acima dos modais da aplicação (1000 a 2000 em uso) e do nível 'modal' do MUI.
const Z_INDEX_DROPDOWN = 2100;

/**
 * Dropdown de projetos com checkboxes ("Selecionar todos" + um por projeto). As marcações ficam num
 * rascunho local; a seleção só vale (onChange) ao fechar o painel — clique fora, Esc, clicar no botão
 * ou "Aplicar". Nenhum marcado ou todos marcados = sem filtro ([]).
 */
const FiltroProjetosMulti: React.FC<FiltroProjetosMultiProps> = ({ projetos, selecionados, onChange }) => {
  const [aberto, setAberto] = useState(false);
  const [rascunho, setRascunho] = useState<Set<string>>(new Set());
  const raizRef = useRef<HTMLDivElement>(null);
  const botaoRef = useRef<HTMLButtonElement>(null);
  const painelRef = useRef<HTMLDivElement>(null);

  const todos = projetos.map((p) => p.uid_pesca_projeto);

  // Refs sempre atuais pros listeners de documento (evita fechar com rascunho/props antigos).
  const rascunhoRef = useRef(rascunho);
  rascunhoRef.current = rascunho;
  const todosRef = useRef(todos);
  todosRef.current = todos;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const selecionadosRef = useRef(selecionados);
  selecionadosRef.current = selecionados;

  const abrir = () => {
    // [] (todos) abre com tudo marcado; senão, só os aplicados
    setRascunho(new Set(selecionados.length ? selecionados : todos));
    setAberto(true);
  };

  const fecharAplicando = () => {
    const nova = normalizarSelecaoProjetos(rascunhoRef.current, todosRef.current);
    const atual = selecionadosRef.current;
    const igual = nova.length === atual.length && nova.every((u) => atual.includes(u));
    if (!igual) onChangeRef.current(nova);
    setAberto(false);
  };

  useEffect(() => {
    if (!aberto) return;
    const fora = (e: MouseEvent) => {
      // o painel vive num portal (fora de raizRef): clique nele não conta como "fora"
      const alvo = e.target as Node;
      if (raizRef.current?.contains(alvo) || painelRef.current?.contains(alvo)) return;
      fecharAplicando();
    };
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') fecharAplicando(); };
    document.addEventListener('mousedown', fora);
    document.addEventListener('keydown', esc);
    return () => {
      document.removeEventListener('mousedown', fora);
      document.removeEventListener('keydown', esc);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aberto]);

  const todosMarcados = todos.length > 0 && todos.every((u) => rascunho.has(u));
  const algumMarcado = todos.some((u) => rascunho.has(u));

  const alternarTodos = () => setRascunho(todosMarcados ? new Set() : new Set(todos));
  const alternar = (uid: string) =>
    setRascunho((prev) => {
      const novo = new Set(prev);
      if (novo.has(uid)) novo.delete(uid); else novo.add(uid);
      return novo;
    });

  const rotulo = rotuloProjetos(selecionados, projetos);

  return (
    <div ref={raizRef} style={{ position: 'relative', flex: '0 0 190px' }}>
      <button
        ref={botaoRef}
        type="button"
        onClick={() => (aberto ? fecharAplicando() : abrir())}
        aria-haspopup="true"
        aria-expanded={aberto}
        style={{
          width: '190px', padding: '8px 12px', borderRadius: '20px', border: '1px solid #ddd', background: '#fff',
          textAlign: 'left', cursor: 'pointer', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontSize: '0.85rem',
        }}
        title={rotulo}
      >
        {rotulo} {aberto ? '▴' : '▾'}
      </button>
      {/* Portal no body (Popper): não é cortado pelo overflow de quadros/cartões que contêm o filtro. */}
      <Popper open={aberto} anchorEl={botaoRef.current} placement="bottom-start" style={{ zIndex: Z_INDEX_DROPDOWN }}>
        <div ref={painelRef} role="group" aria-label="Projetos" style={{
          width: '260px', background: '#fff', border: '1px solid #ddd',
          borderRadius: '8px', marginTop: '2px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', padding: '8px',
        }}>
          <div style={{ maxHeight: '280px', overflowY: 'auto' }}>
            <label style={{ display: 'flex', alignItems: 'center', minHeight: 30, cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}>
              <Checkbox size="small" checked={todosMarcados} indeterminate={algumMarcado && !todosMarcados} onChange={alternarTodos} sx={{ padding: '4px' }} />
              Selecionar todos
            </label>
            {projetos.map((p) => (
              <label key={p.uid_pesca_projeto} style={{ display: 'flex', alignItems: 'center', minHeight: 30, cursor: 'pointer', fontSize: '0.85rem' }}>
                <Checkbox size="small" checked={rascunho.has(p.uid_pesca_projeto)} onChange={() => alternar(p.uid_pesca_projeto)} sx={{ padding: '4px' }} />
                {p.nome}
              </label>
            ))}
            {projetos.length === 0 && <div style={{ padding: '8px', color: '#999', fontSize: '0.85rem' }}>Nenhum projeto disponível.</div>}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
            <button
              type="button"
              onClick={fecharAplicando}
              style={{ background: '#00b37e', color: '#fff', border: 'none', padding: '6px 16px', borderRadius: '14px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' }}
            >
              Aplicar
            </button>
          </div>
        </div>
      </Popper>
    </div>
  );
};

export default FiltroProjetosMulti;
